import { FreightItem, CalculationResult, Language } from '../types';
import { languageNames } from './translations';

async function askGemini(userPrompt: string, systemRole: string = "You are a helpful assistant"): Promise<string> {
  try {
    const response = await fetch("https://lightapp-api.promptvault.work", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        prompt: userPrompt, 
        systemInstruction: systemRole 
      })
    });
    
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    
    const data = await response.json();
    
    if (data.error) throw new Error(data.error);
    return data.answer; 
  } catch (error: any) {
    console.error("Gemini API Request Failed:", error);
    return "System busy, please try again later. (Error: " + error.message + ")";
  }
}

export const getFreightAdvice = async (
  items: FreightItem[],
  result: CalculationResult,
  language: Language
): Promise<string> => {
  // Construct a detailed prompt context
  const itemsDescription = items.map((item, idx) => 
    `Pallet ${idx + 1}: ${item.length}"L x ${item.width}"W x ${item.height}"H @ ${item.weight}lbs (Qty: ${item.quantity}) [Stackable: ${item.isStackable ? 'Yes' : 'No'}] - ${item.description || 'No description'}`
  ).join('\n');

  const targetLangName = languageNames[language];

  const prompt = `
    You are an expert Freight Broker and LTL (Less-Than-Truckload) Logistics Specialist.
    
    The user is checking density, freight class, and linear feet usage.
    
    Shipment Data:
    ${itemsDescription}
    
    Calculated Totals:
    - Total Weight: ${result.totalWeight} lbs
    - Total Volume: ${result.totalCubicFeet.toFixed(2)} cubic feet
    - Calculated Density: ${result.density.toFixed(2)} lbs/cu ft
    - Estimated NMFC Class: ${result.estimatedClass}
    - Linear Feet Used: ~${result.linearFeet.toFixed(1)} ft
    
    Please provide a concise analysis using CURRENT NMFC standards (latest edition).
    
    IMPORTANT: You must answer in ${targetLangName}.
    
    Structure your response:
    1. **Verify the Class**: Confirm if the density-based class matches standard expectations.
    2. **Linear Feet Warning**: If Linear Feet > 12ft, warn about potential "Capacity Load" rules.
    3. **Commodity Checks**: If a description is provided, check if specific NMFC codes apply.
    4. **Optimization**: Suggest packing improvements.
    
    Keep the tone professional and advisory in ${targetLangName}.
  `;

  const systemInstruction = `You are a senior logistics consultant AI. You speak fluent ${targetLangName}.`;

  return await askGemini(prompt, systemInstruction);
};